// Analítica de uso anónima: ingresos (sesiones), minutos de uso, páginas visitadas y
// tiempo en cada una. Identifica al usuario por su pubkey (ver identity.ts). Los eventos
// se mandan a nuestro backend (POST /api/track). Nada de esto sale a relays ni a terceros.
import { getIdentity, isFirstVisit } from './identity';

const BASE = process.env.REACT_APP_API_BASE ?? '';

type Ev = Record<string, unknown>;

function send(events: Ev[]): void {
  if (events.length === 0) return;
  const body = JSON.stringify({ pub: getIdentity().pub, events });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${BASE}/api/track`, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(`${BASE}/api/track`, {
        method: 'POST',
        body,
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
      }).catch(() => {});
    }
  } catch {
    /* sin red: se pierde el evento, no es crítico */
  }
}

let sessionSent = false;
let current = { path: '', at: 0 };

// Una sola vez por carga de la app: registra el "ingreso" (con el nick si ya está elegido).
// admin=true marca al usuario con acceso a /uso → el backend lo excluye de las métricas.
export function sessionStart(nick?: string, admin?: boolean): void {
  if (sessionSent) return;
  sessionSent = true;
  send([
    {
      t: 'session',
      ts: Date.now(),
      first: isFirstVisit() ? 1 : 0,
      nick: nick || undefined,
      admin: admin ? 1 : undefined,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      lang: navigator.language,
    },
  ]);
  getIdentity(); // garantiza que la identidad quede creada
}

// Asocia un nombre/apodo a la pubkey (cuando se elige en el primer ingreso).
export function identify(nick: string): void {
  if (!nick) return;
  send([{ t: 'identify', nick }]);
}

// Marca esta pubkey como admin (tiene acceso a /uso) → se excluye de las estadísticas.
export function flagAdmin(): void {
  send([{ t: 'admin', admin: 1 }]);
}

// Cambió de página: registra el tiempo que estuvo en la anterior y arranca el cronómetro.
export function pageEnter(path: string): void {
  const now = Date.now();
  if (current.path && current.path !== path) {
    send([{ t: 'page', path: current.path, ts: current.at, dwell: now - current.at }]);
  }
  if (current.path !== path) current = { path, at: now };
}

// Al ocultar/cerrar la pestaña: vuelca el tiempo de la página actual (sin doble conteo).
export function flush(): void {
  const now = Date.now();
  if (current.path) {
    send([{ t: 'page', path: current.path, ts: current.at, dwell: now - current.at }]);
    current = { path: current.path, at: now };
  }
}
